import React, { useState, useEffect, useMemo, Suspense } from "react";
import { Modal, Button } from "antd";
import { FullscreenOutlined, WarningOutlined, CloseOutlined } from "@ant-design/icons";
import Loading from "components/shared-components/Loading";
import { getAppImporter } from "configs/appModules";
import { goToBugURL } from "views/app-views/apps/commons";
import "./index.css";

// Alturas de header/footer (deben coincidir con el CSS)
const HEADER_HEIGHT = 72;
const FOOTER_HEIGHT = 64;

// Deriva un título legible desde la ruta cuando no llega uno explícito
function titleFromPath(path) {
  if (!path) return "Actividad";
  const last = path.split("/").filter(Boolean).pop() || "Actividad";
  return last
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
}

/**
 * AppPopup — Modal global que monta una app (Unity WebGL) sin cambiar de ruta,
 * para no perder el contexto de la plataforma que quedó por debajo.
 *
 * Se abre despachando un evento de window:
 *   window.dispatchEvent(new CustomEvent('openApp', { detail: { path, title } }))
 * (mismo patrón que BugReportModal con 'openBugReport').
 *
 * Estructura: header (nombre + cerrar) · stage 16:9 · footer (acciones).
 * El modal se dimensiona al ancho del lienzo 16:9 (sin franjas negras laterales).
 * Una sola instancia Unity viva: modal único + destroyOnClose.
 */
export default function AppPopup() {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState(null);
  const [title, setTitle] = useState("");
  const [dims, setDims] = useState({ w: 0, h: 0 });

  // Escucha el evento de apertura
  useEffect(() => {
    const handler = (e) => {
      const nextPath = e?.detail?.path;
      if (!nextPath || !getAppImporter(nextPath)) return;
      // Quita el foco del canvas actual (evita que Unity siga capturando teclas)
      document.querySelectorAll("canvas").forEach((c) => c.blur());
      setPath(nextPath);
      setTitle(e?.detail?.title || titleFromPath(nextPath));
      setOpen(true);
    };
    window.addEventListener("openApp", handler);
    return () => window.removeEventListener("openApp", handler);
  }, []);

  // Calcula el mayor lienzo 16:9 que cabe (dejando espacio para header + footer)
  useEffect(() => {
    if (!open) return undefined;
    const compute = () => {
      const maxW = window.innerWidth * 0.96;
      const maxH = window.innerHeight * 0.94;
      const availH = Math.max(0, maxH - HEADER_HEIGHT - FOOTER_HEIGHT);
      const w = Math.min(maxW, (availH * 16) / 9);
      const h = (w * 9) / 16;
      setDims({ w, h });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [open]);

  // Resuelve el componente lazy para la ruta actual
  const LazyApp = useMemo(() => {
    if (!path) return null;
    const importer = getAppImporter(path);
    return importer ? React.lazy(importer) : null;
  }, [path]);

  // Pantalla completa sobre el lienzo de Unity dentro del popup
  const handleFullscreen = () => {
    const canvas = document.querySelector(".app-popup-stage canvas");
    if (canvas && canvas.requestFullscreen) canvas.requestFullscreen();
  };

  // Avisa a la plataforma que la actividad se cerró, para que refresque el
  // progreso (Home escucha 'appClosed'). Se emite en afterClose, es decir
  // después de que destroyOnClose desmontó la instancia de Unity.
  const handleAfterClose = () => {
    window.dispatchEvent(new CustomEvent("appClosed", { detail: { path, title } }));
  };

  // Cierre con confirmación (se pierde el progreso interno de la simulación)
  const requestClose = () => {
    Modal.confirm({
      title: "¿Salir de la actividad?",
      content: "Se perderá el progreso no guardado de la simulación.",
      okText: "Salir",
      cancelText: "Seguir",
      okButtonProps: { danger: true },
      onOk: () => setOpen(false),
    });
  };

  return (
    <Modal
      open={open}
      onCancel={requestClose}
      afterClose={handleAfterClose}
      footer={null}
      closable={false}
      centered
      width={dims.w || "96vw"}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      className="app-popup-modal"
      styles={{
        content: { padding: 0, overflow: "hidden" },
        body: {
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: "#0a0e1a",
        },
      }}
    >
      {/* Header: nombre de la actividad + cerrar */}
      <div className="app-popup-header">
        <div>
          <div className="app-popup-header-overline">Simulador · Unity WebGL</div>
          <div className="app-popup-header-title">{title}</div>
        </div>
        <button className="app-popup-close" onClick={requestClose} aria-label="Cerrar">
          <CloseOutlined />
        </button>
      </div>

      {/* Stage 16:9 */}
      {LazyApp && (
        <div className="app-popup-stage" style={{ height: dims.h || "auto" }}>
          <Suspense fallback={<Loading cover="content" />}>
            {/* key por path: al cambiar de app, la saliente se desmonta
                (dispara detachAndUnloadImmediate) antes de montar la nueva */}
            <LazyApp key={path} />
          </Suspense>
        </div>
      )}

      {/* Footer: acciones */}
      <div className="app-popup-footer">
        <span className="app-popup-footer-hint" />
        <div className="app-popup-footer-actions">
          <Button className="app-popup-report-btn" icon={<WarningOutlined />} onClick={goToBugURL}>
            Reportar Error
          </Button>
          <Button type="primary" icon={<FullscreenOutlined />} onClick={handleFullscreen}>
            Pantalla Completa
          </Button>
        </div>
      </div>
    </Modal>
  );
}
